import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CloudUpload,
  Download,
  FileText,
  Film,
  Grid2x2,
  HardDrive,
  HardDriveDownload,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Printer,
  Save,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { useExportSheet } from "../features/export-sheet/index.js";
import {
  cloudExportTarget,
  type ExportTarget,
  localExportTarget,
} from "../features/media/export.js";
import { ExportMenu } from "../features/media/export-menu.jsx";
import { PdfViewer } from "../features/media/pdf-viewer.jsx";
import { PosterDialog } from "../features/media/poster/poster-dialog.jsx";
import { type PrintRequest, usePrint } from "../features/print/index.js";
import { apiFetch } from "../lib/api.js";
import {
  deleteLocalMedia,
  importLocalMediaFromBytes,
  isLocalMediaAvailable,
  isStoragePersisted,
  isTauri,
  type LocalMediaItem,
  listLocalMedia,
  localMediaToBlobUrl,
  readLocalMediaBytes,
  requestStoragePersist,
  saveLocalMedia,
} from "../lib/local-media.js";
import { useSync } from "../providers/sync-provider";

/**
 * Allowed upload MIME types. Mirrors the server-side `ALLOWED_MIME` in
 * `apps/server/src/routes/media.ts` so a rejected upload doesn't waste a
 * round-trip. Keep both lists in sync.
 */
const ALLOWED_MIME = new Set<string>([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/avif",
  "image/gif",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "application/pdf",
]);
const ALLOWED_ACCEPT = Array.from(ALLOWED_MIME).join(",");

/* ─────────────────────────── types ─────────────────────────── */

interface MediaItem {
  id: string;
  orgId: string;
  ownerKind: string;
  ownerId: string;
  s3Key: string;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  updatedAt: string;
  publicUrl: string;
  filename: string;
}

interface ListResponse {
  ok: true;
  items: MediaItem[];
}

interface PresignResponse {
  url: string;
  s3Key: string;
  headers: Record<string, string>;
  publicUrl: string;
  expiresIn: number;
}

interface RegisterResponse {
  ok: true;
  mediaRef: MediaItem;
}

interface ViewUrlResponse {
  ok: true;
  url: string;
  expiresIn: number;
}

interface StorageResponse {
  ok: true;
  orgId: string;
  used: number;
  limit: number;
  remaining: number;
}

type UnifiedItem =
  | { source: "cloud"; id: string; data: MediaItem }
  | { source: "local"; id: string; data: LocalMediaItem };

type UploadTarget = "cloud" | "local";

/* ─────────────────────────── page ─────────────────────────── */

/**
 * Mediathek (media browser).
 *
 * Cloud items live on the server (MinIO/S3) and sync across devices — they
 * count against the org's storage quota. The native desktop/mobile build
 * adds an optional *local-only* mode: files stay on the device, never
 * upload, and don't touch the quota. Local items are tracked in the
 * `local_media` SQLite table and surface in the same grid with a
 * hard-drive glyph instead of the cloud one.
 */
export function MediaPage() {
  const qc = useQueryClient();
  const { db, orgId } = useSync();
  const localAvailable = isLocalMediaAvailable();
  const isWebLocal = localAvailable && !isTauri();

  /**
   * Gate the persistence disclaimer behind a localStorage flag so the
   * (somewhat wordy) explanation only appears the first time someone
   * saves locally in a given browser profile. We persist *acknowledgement*
   * — not the browser's `persisted` state — because some browsers never
   * report `true` even after silently granting persistence.
   */
  const PERSIST_ACK_KEY = "coach.localMedia.persistAcknowledged.v1";
  function hasAcknowledgedPersist(): boolean {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(PERSIST_ACK_KEY) === "1";
    } catch {
      return false;
    }
  }
  function markPersistAcknowledged() {
    try {
      window.localStorage.setItem(PERSIST_ACK_KEY, "1");
    } catch {
      /* private mode / quota — ignore */
    }
  }

  const fileRef = useRef<HTMLInputElement | null>(null);
  const pendingTarget = useRef<UploadTarget>("cloud");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDialog, setErrorDialog] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [picker, setPicker] = useState(false);
  const [persistOpen, setPersistOpen] = useState(false);
  /** Resolves the persist-disclaimer dialog. */
  const persistResolveRef = useRef<((ok: boolean) => void) | null>(null);

  const cloudList = useQuery({
    queryKey: ["media", "list"],
    queryFn: () => apiFetch<ListResponse>("/api/v1/media"),
  });

  const storageQ = useQuery({
    queryKey: ["me", "storage"],
    queryFn: () => apiFetch<StorageResponse>("/api/v1/me/storage"),
  });

  const localList = useQuery({
    queryKey: ["media", "local", orgId],
    queryFn: () => listLocalMedia(db!, orgId!),
    enabled: localAvailable && Boolean(db) && Boolean(orgId),
  });

  const removeCloud = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/api/v1/media/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["media", "list"] }),
        qc.invalidateQueries({ queryKey: ["me", "storage"] }),
      ]);
    },
  });

  const removeLocal = useMutation({
    mutationFn: (item: LocalMediaItem) => {
      if (!db) throw new Error("Lokale Datenbank nicht bereit.");
      return deleteLocalMedia(db, item);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media", "local", orgId] }),
  });

  const renameCloud = useMutation({
    mutationFn: (args: { id: string; filename: string }) =>
      apiFetch<RegisterResponse>(`/api/v1/media/${args.id}`, {
        method: "PATCH",
        body: JSON.stringify({ filename: args.filename }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media", "list"] }),
  });

  /**
   * Move a local file to the cloud. Uploads first, deletes locally only
   * on success — a failed upload leaves the local copy intact.
   */
  const moveLocalToCloud = useMutation({
    mutationFn: async (item: LocalMediaItem) => {
      if (!db) throw new Error("Lokale Datenbank nicht bereit.");
      const remaining = storageQ.data?.remaining ?? Infinity;
      const limit = storageQ.data?.limit ?? 0;
      if (limit > 0 && item.bytes > remaining) {
        throw new Error(
          `Cloud-Speicher reicht nicht: ${formatBytes(item.bytes)} benötigt, nur ${formatBytes(remaining)} verfügbar.`,
        );
      }
      const bytes = await readLocalMediaBytes(item);
      const presign = await apiFetch<PresignResponse>("/api/v1/media/upload-url", {
        method: "POST",
        body: JSON.stringify({ mime: item.mime, bytes: item.bytes }),
      });
      const buf = new Uint8Array(bytes.byteLength);
      buf.set(bytes);
      const put = await fetch(presign.url, {
        method: "PUT",
        headers: presign.headers,
        body: buf,
      });
      if (!put.ok) {
        const body = await put.text().catch(() => "");
        throw new Error(`PUT → ${put.status} ${body}`);
      }
      await apiFetch<RegisterResponse>("/api/v1/media/register", {
        method: "POST",
        body: JSON.stringify({
          s3Key: presign.s3Key,
          mime: item.mime,
          bytes: item.bytes,
          filename: stripExtension(item.filename),
        }),
      });
      // Only now is it safe to drop the local copy.
      await deleteLocalMedia(db, item);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["media", "list"] }),
        qc.invalidateQueries({ queryKey: ["media", "local", orgId] }),
        qc.invalidateQueries({ queryKey: ["me", "storage"] }),
      ]);
    },
  });

  /**
   * Move a cloud file to local storage. Downloads via signed URL, writes
   * locally, then deletes the cloud copy. A failed local write leaves the
   * cloud copy intact.
   */
  const moveCloudToLocal = useMutation({
    mutationFn: async (item: MediaItem) => {
      if (!db || !orgId) throw new Error("Lokale Datenbank nicht bereit.");
      const view = await apiFetch<ViewUrlResponse>(`/api/v1/media/${item.id}/view-url`);
      const res = await fetch(view.url);
      if (!res.ok) {
        throw new Error(`Download → ${res.status}`);
      }
      const buf = new Uint8Array(await res.arrayBuffer());
      await importLocalMediaFromBytes(db, orgId, {
        filename: item.filename,
        mime: item.mime,
        bytes: buf,
      });
      // Only now is it safe to remove the cloud copy.
      await apiFetch<{ ok: true }>(`/api/v1/media/${item.id}`, {
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["media", "list"] }),
        qc.invalidateQueries({ queryKey: ["media", "local", orgId] }),
        qc.invalidateQueries({ queryKey: ["me", "storage"] }),
      ]);
    },
  });

  /** Merged + sorted (newest first) view of cloud + local items. */
  const items = useMemo<UnifiedItem[]>(() => {
    const cloud: UnifiedItem[] = (cloudList.data?.items ?? []).map((m) => ({
      source: "cloud",
      id: `c:${m.id}`,
      data: m,
    }));
    const local: UnifiedItem[] = (localList.data ?? []).map((m) => ({
      source: "local",
      id: `l:${m.id}`,
      data: m,
    }));
    return [...cloud, ...local].sort((a, b) => {
      const ta = a.source === "cloud" ? a.data.updatedAt : a.data.createdAt;
      const tb = b.source === "cloud" ? b.data.updatedAt : b.data.createdAt;
      return tb.localeCompare(ta);
    });
  }, [cloudList.data?.items, localList.data]);

  const totalBytes = useMemo(() => items.reduce((acc, it) => acc + it.data.bytes, 0), [items]);

  function startUpload(target: UploadTarget) {
    pendingTarget.current = target;
    setPicker(false);
    void beginUploadFlow(target);
  }

  /**
   * On the web, ensure the user has been shown the persistence disclaimer
   * and the browser has granted persistent storage for this origin before
   * we let them save anything locally. Tauri skips this entirely.
   */
  async function beginUploadFlow(target: UploadTarget) {
    if (target === "local" && isWebLocal && !hasAcknowledgedPersist()) {
      const persisted = await isStoragePersisted();
      if (!persisted) {
        const ok = await new Promise<boolean>((resolve) => {
          persistResolveRef.current = resolve;
          setPersistOpen(true);
        });
        if (!ok) {
          pendingTarget.current = "cloud";
          return;
        }
        await requestStoragePersist();
      }
      markPersistAcknowledged();
    }
    fileRef.current?.click();
  }

  function onUploadClick() {
    if (localAvailable) {
      setPicker(true);
    } else {
      pendingTarget.current = "cloud";
      fileRef.current?.click();
    }
  }

  async function handleFiles(files: File[]) {
    if (files.length === 0) {
      console.debug("[mediathek] handleFiles: empty list, aborting");
      return;
    }
    // Hard filter on the client side too — the server enforces the same
    // list, but rejecting up-front avoids a doomed presign round-trip and
    // gives the user an immediate, descriptive error.
    const rejected = files.filter((f) => !ALLOWED_MIME.has(f.type));
    const accepted = files.filter((f) => ALLOWED_MIME.has(f.type));
    if (rejected.length) {
      const names = rejected.map((f) => `„${f.name}“ (${f.type || "unbekannter Typ"})`).join(", ");
      setError(
        `Format nicht erlaubt: ${names}. Erlaubt sind Bilder (PNG/JPEG/WEBP/AVIF/GIF), Videos (MP4/WebM/MOV) und PDFs.`,
      );
      if (accepted.length === 0) return;
    } else {
      setError(null);
    }
    const target = pendingTarget.current;

    // Quota pre-flight for cloud uploads: refuse before any presign call
    // if the batch wouldn't fit. Surfaces as an error popup since the
    // user asked for an explicit dialog when over quota.
    if (target === "cloud") {
      const remaining = storageQ.data?.remaining ?? Infinity;
      const limit = storageQ.data?.limit ?? 0;
      const total = accepted.reduce((acc, f) => acc + f.size, 0);
      if (remaining <= 0 && limit > 0) {
        setErrorDialog(
          `Cloud-Speicher voll (${formatBytes(storageQ.data!.used)} / ${formatBytes(limit)}). Lösche zuerst andere Dateien, bevor du neue hochlädst.`,
        );
        return;
      }
      if (total > remaining) {
        setErrorDialog(
          `Upload zu groß: ${formatBytes(total)} benötigt, aber nur ${formatBytes(remaining)} verfügbar. Lösche zuerst andere Dateien oder wähle kleinere Dateien aus.`,
        );
        return;
      }
    }

    setUploadBusy(true);
    try {
      for (const file of accepted) {
        console.debug("[mediathek] uploading", {
          name: file.name,
          size: file.size,
          mime: file.type,
          target,
        });
        if (target === "local") {
          if (!db || !orgId) {
            throw new Error("Lokale Datenbank nicht bereit.");
          }
          await saveLocalMedia(db, orgId, file);
        } else {
          const presign = await apiFetch<PresignResponse>("/api/v1/media/upload-url", {
            method: "POST",
            body: JSON.stringify({ mime: file.type, bytes: file.size }),
          });
          console.debug("[mediathek] presign ok", presign.s3Key);
          const put = await fetch(presign.url, {
            method: "PUT",
            headers: presign.headers,
            body: file,
          });
          if (!put.ok) {
            const body = await put.text().catch(() => "");
            throw new Error(`PUT ${file.name} → ${put.status} ${body}`);
          }
          await apiFetch<RegisterResponse>("/api/v1/media/register", {
            method: "POST",
            body: JSON.stringify({
              s3Key: presign.s3Key,
              mime: file.type,
              bytes: file.size,
              filename: stripExtension(file.name),
            }),
          });
          console.debug("[mediathek] registered", file.name);
        }
      }
      if (target === "local") {
        await qc.invalidateQueries({ queryKey: ["media", "local", orgId] });
      } else {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["media", "list"] }),
          qc.invalidateQueries({ queryKey: ["me", "storage"] }),
        ]);
      }
    } catch (e) {
      console.error("[mediathek] upload failed", e);
      const msg =
        e instanceof Error
          ? e.message.includes("413")
            ? "Speicherlimit erreicht — bitte zuerst andere Dateien löschen."
            : e.message
          : "Upload fehlgeschlagen.";
      // Quota / size errors get a blocking popup; everything else stays
      // as the inline error pill.
      if (msg.includes("Speicherlimit") || msg.includes("413")) {
        setErrorDialog(msg);
      } else {
        setError(msg);
      }
    } finally {
      setUploadBusy(false);
    }
  }

  const isLoading = cloudList.isLoading || (localAvailable && localList.isLoading);
  const total = items.length;

  const { confirm, dialog: confirmDialog } = useConfirm();
  const { openPrintDialog, dialog: printDialog } = usePrint();
  const { openExportSheet, sheet: exportSheet } = useExportSheet();
  /** Open poster dialog; the target is created once per click so bytes load once. */
  const [poster, setPoster] = useState<{
    target: ExportTarget;
    kind: "image" | "pdf";
    page: number;
    filename: string;
  } | null>(null);

  async function requestDelete(item: UnifiedItem): Promise<boolean> {
    const name = item.data.filename;
    const ok = await confirm({
      title: "Medium löschen?",
      message:
        item.source === "cloud"
          ? `„${name}“ wirklich löschen? Das gibt den Cloud-Speicher wieder frei.`
          : `„${name}“ wirklich vom Gerät löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Löschen",
    });
    if (!ok) return false;
    if (item.source === "cloud") {
      if (removeCloud.isPending) return false;
      await removeCloud.mutateAsync(item.data.id);
    } else {
      if (removeLocal.isPending) return false;
      await removeLocal.mutateAsync(item.data);
    }
    return true;
  }

  /**
   * Move a single item between cloud and local tiers. Confirms first so
   * the user understands the destination („lokal = nur dieses Gerät“)
   * and that the source copy goes away.
   */
  async function requestMove(item: UnifiedItem): Promise<boolean> {
    if (item.source === "local") {
      const ok = await confirm({
        title: "In Cloud verschieben?",
        message: `„${item.data.filename}“ wird in die Cloud hochgeladen und danach lokal gelöscht (${formatBytes(item.data.bytes)}). Cloud-Speicher wird belegt.`,
        confirmLabel: "Verschieben",
      });
      if (!ok) return false;
      try {
        await moveLocalToCloud.mutateAsync(item.data);
        return true;
      } catch (e) {
        setErrorDialog(e instanceof Error ? e.message : "Verschieben in die Cloud fehlgeschlagen.");
        return false;
      }
    }
    // cloud -> local
    if (!localAvailable) return false;
    const ok = await confirm({
      title: "Lokal speichern?",
      message: `„${item.data.filename}“ wird auf dieses Gerät verschoben und aus der Cloud gelöscht (${formatBytes(item.data.bytes)}). Danach ist die Datei nur noch ${isWebLocal ? "in diesem Browser-Profil" : "auf diesem Gerät"} verfügbar.`,
      confirmLabel: "Verschieben",
    });
    if (!ok) return false;
    if (isWebLocal) {
      const persisted = await isStoragePersisted();
      if (!persisted && !hasAcknowledgedPersist()) {
        const okPersist = await new Promise<boolean>((resolve) => {
          persistResolveRef.current = resolve;
          setPersistOpen(true);
        });
        if (!okPersist) return false;
        await requestStoragePersist();
        markPersistAcknowledged();
      }
    }
    try {
      await moveCloudToLocal.mutateAsync(item.data);
      return true;
    } catch (e) {
      setErrorDialog(
        e instanceof Error ? e.message : "Verschieben in den lokalen Speicher fehlgeschlagen.",
      );
      return false;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ── toolbar ─────────────────────────────────────────────── */}
      <section className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-6 py-5 sm:px-7">
        <div className="min-w-0 flex-1">
          <h1 className="home-display text-[20px] font-bold" style={{ color: "#f4f5f3" }}>
            Bilder &amp; Videos
          </h1>
          <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
            {isLoading
              ? "Lade…"
              : `${total} Datei${total === 1 ? "" : "en"} · ${formatBytes(totalBytes)}`}
          </p>
        </div>

        <StorageMeter storage={storageQ.data ?? null} />

        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ALLOWED_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const input = e.target;
            const files = input.files ? Array.from(input.files) : [];
            input.value = "";
            console.debug("[mediathek] file input change", {
              count: files.length,
              target: pendingTarget.current,
            });
            void handleFiles(files);
          }}
        />

        <button
          type="button"
          onClick={onUploadClick}
          disabled={uploadBusy}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold transition-colors disabled:opacity-60"
          style={{
            background: "var(--color-pitch-lime)",
            color: "#1d3700",
            boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
          }}
        >
          {uploadBusy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
          Hochladen
        </button>
      </section>

      {error && (
        <p
          className="rounded-2xl border px-4 py-2 text-[12px]"
          style={{
            borderColor: "rgba(255,107,107,0.30)",
            background: "rgba(255,107,107,0.08)",
            color: "var(--color-error, #ff6b6b)",
          }}
        >
          {error}
        </p>
      )}

      {/* ── grid ─────────────────────────────────────────────── */}
      {isLoading ? (
        <EmptyState text="Lade Medien…" />
      ) : items.length === 0 ? (
        <EmptyState text="Noch keine Medien hochgeladen. Klick auf „Hochladen“, um den ersten Upload zu starten." />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((item, idx) => (
            <MediaCard
              key={item.id}
              item={item}
              canMove={item.source === "local" || (item.source === "cloud" && localAvailable)}
              moveBusy={moveLocalToCloud.isPending || moveCloudToLocal.isPending}
              onOpen={() => setPreviewIndex(idx)}
              onDelete={() => requestDelete(item)}
              onMove={() => requestMove(item)}
            />
          ))}
        </div>
      )}

      {previewIndex !== null && items[previewIndex] && (
        <Lightbox
          items={items}
          index={previewIndex}
          onIndexChange={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
          onDelete={async (item) => {
            const ok = await requestDelete(item);
            if (ok) setPreviewIndex(null);
          }}
          onRename={async (item, name) => {
            if (item.source !== "cloud") return;
            await renameCloud.mutateAsync({ id: item.data.id, filename: name });
          }}
          onMove={async (item) => {
            const ok = await requestMove(item);
            if (ok) setPreviewIndex(null);
          }}
          canMove={(item) => item.source === "local" || (item.source === "cloud" && localAvailable)}
          moveBusy={moveLocalToCloud.isPending || moveCloudToLocal.isPending}
          onRequestPrint={(req) => {
            void openPrintDialog(req);
          }}
          onRequestPoster={(item, currentPage) => {
            setPoster({
              target: exportTargetFor(item),
              kind: item.data.mime === "application/pdf" ? "pdf" : "image",
              page: currentPage,
              filename: item.data.filename,
            });
          }}
        />
      )}

      {poster && (
        <PosterDialog
          target={poster.target}
          kind={poster.kind}
          initialPage={poster.page}
          defaultFilename={`${stripExtension(poster.filename)}-poster`}
          onExported={async (bytes, name) => {
            setPoster(null);
            await openExportSheet({
              bytes,
              filename: name,
              mime: "application/pdf",
              title: "Poster exportieren",
            });
          }}
          onClose={() => setPoster(null)}
        />
      )}

      {picker && (
        <UploadTargetPicker
          localScope={isWebLocal ? "browser" : "device"}
          onPick={startUpload}
          onClose={() => setPicker(false)}
        />
      )}
      {persistOpen && (
        <PersistDisclaimerDialog
          onCancel={() => {
            persistResolveRef.current?.(false);
            persistResolveRef.current = null;
            setPersistOpen(false);
          }}
          onConfirm={() => {
            persistResolveRef.current?.(true);
            persistResolveRef.current = null;
            setPersistOpen(false);
          }}
        />
      )}
      {errorDialog && <ErrorDialog message={errorDialog} onClose={() => setErrorDialog(null)} />}
      {confirmDialog}
      {printDialog}
      {exportSheet}
    </div>
  );
}

/* ─────────────────────────── upload target picker ─────────────────────────── */

function UploadTargetPicker({
  localScope,
  onPick,
  onClose,
}: {
  localScope: "device" | "browser";
  onPick: (target: UploadTarget) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="glass-floating relative w-full max-w-md overflow-hidden rounded-3xl p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="home-display text-[18px] font-bold" style={{ color: "#f4f5f3" }}>
          Wohin damit?
        </h2>
        <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
          Wähle, ob die Datei auf dem Server gespeichert oder nur auf diesem Gerät behalten werden
          soll.
        </p>

        <div className="mt-5 flex flex-col gap-3">
          <button
            type="button"
            onClick={() => onPick("cloud")}
            className="flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors hover:bg-white/5"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
          >
            <span
              className="grid size-10 shrink-0 place-items-center rounded-xl"
              style={{
                background: "rgba(151,252,0,0.15)",
                color: "var(--color-pitch-lime)",
              }}
            >
              <Cloud className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold" style={{ color: "#f4f5f3" }}>
                Cloud (geräteübergreifend)
              </span>
              <span
                className="block text-[11px]"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                Auf dem Server gespeichert, auf allen Geräten verfügbar, zählt zum Team-Speicher.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => onPick("local")}
            className="flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors hover:bg-white/5"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
          >
            <span
              className="grid size-10 shrink-0 place-items-center rounded-xl"
              style={{
                background: "rgba(120,170,255,0.15)",
                color: "#7eb6ff",
              }}
            >
              <HardDrive className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold" style={{ color: "#f4f5f3" }}>
                {localScope === "browser"
                  ? "Lokal (nur dieser Browser)"
                  : "Lokal (nur dieses Gerät)"}
              </span>
              <span
                className="block text-[11px]"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                {localScope === "browser"
                  ? "Bleibt offline im Browser-Profil, kein Upload, kein Team-Speicher."
                  : "Bleibt offline auf dem Gerät, kein Upload, kein Speicher­vor­rat im Team."}
              </span>
            </span>
          </button>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-3 py-1.5 text-[12px]"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── persist disclaimer ─────────────────────────── */

/**
 * Blocking disclaimer shown the first time the user wants to save anything
 * locally in a web browser. Explains what "lokal" means on the web (per
 * browser-profile, can be evicted if storage gets tight, no cross-device
 * sync) and warns that the browser may show its own permission prompt
 * right after they confirm. We only trigger the browser-level
 * `navigator.storage.persist()` *after* the user accepts this dialog.
 */
function PersistDisclaimerDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-210 grid place-items-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      role="presentation"
    >
      <div
        className="glass-floating relative w-full max-w-md overflow-hidden rounded-3xl p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="persist-disclaimer-title"
      >
        <div className="flex items-start gap-3">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-xl"
            style={{
              background: "rgba(120,170,255,0.15)",
              color: "#7eb6ff",
            }}
          >
            <ShieldCheck className="size-5" />
          </span>
          <div className="min-w-0">
            <h2
              id="persist-disclaimer-title"
              className="home-display text-[18px] font-bold"
              style={{ color: "#f4f5f3" }}
            >
              Lokal speichern aktivieren
            </h2>
            <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              Im Browser werden Dateien in einem geschützten Speicher dieses Browser-Profils
              abgelegt.
            </p>
          </div>
        </div>

        <ul className="mt-5 space-y-2 text-[12px] leading-relaxed" style={{ color: "#dde0d4" }}>
          <li className="flex gap-2">
            <span style={{ color: "#7eb6ff" }}>•</span>
            <span>
              Die Dateien sind <strong>nur in diesem Browser-Profil</strong> verfügbar — nicht auf
              anderen Geräten und nicht in anderen Browsern.
            </span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: "#7eb6ff" }}>•</span>
            <span>
              <strong>„Browserdaten löschen"</strong> entfernt auch deine lokalen Medien.
            </span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: "#7eb6ff" }}>•</span>
            <span>
              Wir bitten den Browser, deine lokalen Medien{" "}
              <strong>nicht automatisch zu löschen</strong>, wenn der Speicher knapp wird. Dafür
              zeigt dein Browser direkt nach dem Bestätigen unter Umständen ein eigenes
              Genehmigungs- Popup — bitte dort auf „Erlauben" klicken.
            </span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: "#7eb6ff" }}>•</span>
            <span>
              Lokale Dateien zählen <strong>nicht</strong> zum Cloud-Speicher deines Teams.
            </span>
          </li>
        </ul>

        <div
          className="mt-5 rounded-2xl px-3 py-2 text-[11px] leading-relaxed"
          style={{
            background: "rgba(255,184,0,0.10)",
            color: "#ffd266",
            border: "1px solid rgba(255,184,0,0.25)",
          }}
        >
          Tipp: Wenn du Dateien geräteübergreifend brauchst, nutze stattdessen den Cloud-Upload —
          der synchronisiert über alle Geräte hinweg.
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-3 py-2 text-[12px]"
            style={{
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--color-on-surface-variant)",
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-semibold"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#1d3700",
              boxShadow: "0 6px 16px -4px rgba(151,252,0,0.45)",
            }}
          >
            <ShieldCheck className="size-4" />
            Verstanden &amp; aktivieren
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── tile ─────────────────────────── */

function MediaCard({
  item,
  canMove,
  moveBusy,
  onOpen,
  onDelete,
  onMove,
}: {
  item: UnifiedItem;
  canMove: boolean;
  moveBusy: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onMove: () => void;
}) {
  const mime = item.data.mime;
  const filename = item.data.filename;
  const bytes = item.data.bytes;
  const kind = mediaKind(mime);

  return (
    <article
      className="glass-floating group relative overflow-hidden rounded-2xl"
      style={{ aspectRatio: "1 / 1" }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="absolute inset-0 grid place-items-center"
        aria-label={`Vorschau von ${filename}`}
      >
        {kind === "image" && item.source === "cloud" ? (
          <img
            src={item.data.publicUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : kind === "image" && item.source === "local" ? (
          <LocalThumb item={item.data} />
        ) : (
          <div
            className="flex flex-col items-center gap-2"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            {kind === "video" ? <Film className="size-10" /> : <FileText className="size-10" />}
            <span className="px-3 text-center text-[11px] uppercase tracking-[0.12em]">
              {mime.split("/")[1] ?? mime}
            </span>
          </div>
        )}
      </button>

      {/* source chip */}
      {item.source === "cloud" ? (
        <span
          className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.12em]"
          style={{
            background: "rgba(10,12,8,0.65)",
            color: "var(--color-pitch-lime)",
            backdropFilter: "blur(8px)",
          }}
          title="Auf dem Server gespeichert"
        >
          <Cloud className="size-3" /> Cloud
        </span>
      ) : (
        <span
          className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.12em]"
          style={{
            background: "rgba(10,12,8,0.65)",
            color: "#7eb6ff",
            backdropFilter: "blur(8px)",
          }}
          title="Nur auf diesem Gerät"
        >
          <HardDrive className="size-3" /> Lokal
        </span>
      )}

      {/* delete */}
      <button
        type="button"
        onClick={onDelete}
        aria-label="Löschen"
        className="absolute right-2 top-2 grid size-8 place-items-center rounded-full opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-within:opacity-100"
        style={{
          background: "rgba(10,12,8,0.7)",
          color: "#ff8a8a",
          backdropFilter: "blur(8px)",
        }}
      >
        <Trash2 className="size-4" />
      </button>

      {/* move between tiers */}
      {canMove && (
        <button
          type="button"
          onClick={onMove}
          disabled={moveBusy}
          aria-label={item.source === "local" ? "In Cloud verschieben" : "Lokal speichern"}
          title={
            item.source === "local"
              ? "In Cloud verschieben"
              : "Auf dieses Gerät / diesen Browser verschieben"
          }
          className="absolute right-11 top-2 grid size-8 place-items-center rounded-full opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-within:opacity-100 disabled:opacity-50"
          style={{
            background: "rgba(10,12,8,0.7)",
            color: item.source === "local" ? "var(--color-pitch-lime)" : "#7eb6ff",
            backdropFilter: "blur(8px)",
          }}
        >
          {moveBusy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : item.source === "local" ? (
            <CloudUpload className="size-4" />
          ) : (
            <HardDriveDownload className="size-4" />
          )}
        </button>
      )}

      {/* footer */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 px-2.5 py-2"
        style={{
          background: "linear-gradient(180deg, transparent 0%, rgba(10,12,8,0.85) 70%)",
          color: "#f4f5f3",
        }}
      >
        <p className="truncate text-[11px] font-medium" title={filename}>
          {filename}
        </p>
        <p className="text-[10px]" style={{ color: "var(--color-on-surface-variant)" }}>
          {formatBytes(bytes)}
        </p>
      </div>
    </article>
  );
}

/** Lazy-loads a local file as a Blob URL for thumbnail rendering. */
function LocalThumb({ item }: { item: LocalMediaItem }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    void localMediaToBlobUrl(item)
      .then((u) => {
        if (!active) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      })
      .catch(() => {
        /* ignore — fallback icon stays */
      });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [item.id]);
  if (!url) {
    return (
      <div style={{ color: "var(--color-on-surface-variant)" }}>
        <ImageIcon className="size-10" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
    />
  );
}

/* ─────────────────────────── lightbox ─────────────────────────── */

/** Builds the right `ExportTarget` (cloud signed-URL vs local-bytes) for an item. */
function exportTargetFor(item: UnifiedItem): ExportTarget {
  if (item.source === "cloud") {
    return cloudExportTarget({
      id: item.data.id,
      mime: item.data.mime,
      filename: item.data.filename,
    });
  }
  return localExportTarget(item.data);
}

/**
 * Fullscreen media viewer. Renders the active item large, supports keyboard
 * + on-screen navigation (prev/next), browser fullscreen, download/open in
 * new tab, and inline delete. Image/video render natively; PDFs use an
 * `<iframe>` so the browser's built-in viewer takes over.
 */
function Lightbox({
  items,
  index,
  onIndexChange,
  onClose,
  onDelete,
  onRename,
  onMove,
  canMove,
  moveBusy,
  onRequestPrint,
  onRequestPoster,
}: {
  items: UnifiedItem[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  onDelete: (item: UnifiedItem) => void | Promise<void>;
  onRename?: (item: UnifiedItem, name: string) => void | Promise<void>;
  onMove?: (item: UnifiedItem) => void | Promise<void>;
  canMove?: (item: UnifiedItem) => boolean;
  moveBusy?: boolean;
  /**
   * Opens the central print dialog for the active stage. Each stage
   * forwards its own resolved src (image URL, PDF URL + page metadata)
   * so the dialog has everything it needs without a second fetch.
   */
  onRequestPrint?: (req: PrintRequest) => void;
  /** Opens the poster dialog; `currentPage` is the PDF page in view (1 otherwise). */
  onRequestPoster?: (item: UnifiedItem, currentPage: number) => void;
}) {
  const item = items[index];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFs, setIsFs] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  /** Non-blocking toast for export failures — auto-clears after a few s. */
  const [exportError, setExportError] = useState<string | null>(null);
  useEffect(() => {
    if (!exportError) return;
    const t = window.setTimeout(() => setExportError(null), 5_000);
    return () => window.clearTimeout(t);
  }, [exportError]);

  /**
   * The current stage publishes a print-ready payload here (image src,
   * or PDF src + page info). The header Print button is only enabled
   * when this is non-null — keeps the toolbar consistent across all
   * media types without per-stage buttons.
   */
  // The current stage publishes/clears this value itself on (re)mount; we
  // intentionally do NOT clear here on item change, because the parent
  // useEffect would race the child's mount effect (children fire first)
  // and wipe the image src that StageContent just published. The
  // `key={item.id}` on <LightboxStage> guarantees a fresh StageContent
  // mount per item, so its effect always publishes the correct payload.
  const [printRequest, setPrintRequest] = useState<PrintRequest | null>(null);

  // Reset rename state whenever the active item changes (navigate / close).
  useEffect(() => {
    setEditing(false);
    setDraftName("");
    setRenameBusy(false);
  }, [item?.id]);

  const goPrev = () => onIndexChange((index - 1 + items.length) % items.length);
  const goNext = () => onIndexChange((index + 1) % items.length);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't hijack arrows / Esc while the rename input is focused —
      // the input has its own handlers.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        return;
      }
      if (e.key === "Escape") {
        if (document.fullscreenElement) {
          void document.exitFullscreen();
        } else {
          onClose();
        }
      } else if (e.key === "ArrowLeft") {
        goPrev();
      } else if (e.key === "ArrowRight") {
        goNext();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length]);

  useEffect(() => {
    function onFs() {
      setIsFs(Boolean(document.fullscreenElement));
    }
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  async function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      /* user gesture missing or unsupported — ignore */
    }
  }

  if (!item) return null;
  const filename = item.data.filename;
  const mime = item.data.mime;
  const bytes = item.data.bytes;
  const hasMany = items.length > 1;
  const canRename = Boolean(onRename) && item.source === "cloud";

  async function commitRename() {
    if (!onRename || !item) return;
    const next = draftName.trim();
    if (!next || next === filename) {
      setEditing(false);
      return;
    }
    setRenameBusy(true);
    try {
      await onRename(item, next);
      setEditing(false);
    } finally {
      setRenameBusy(false);
    }
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-200 flex flex-col"
      style={{
        background: "rgba(6,8,5,0.92)",
        backdropFilter: "blur(12px)",
      }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      {/* top bar */}
      <header
        className="glass-floating relative z-10 m-3 flex items-center gap-3 rounded-2xl px-4 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              type="text"
              value={draftName}
              disabled={renameBusy}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setEditing(false);
                }
              }}
              onFocus={(e) => {
                e.currentTarget.select();
              }}
              className="w-full rounded-lg px-2 py-1 text-[13px] font-semibold outline-none"
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(151,252,0,0.45)",
                color: "#f4f5f3",
              }}
            />
          ) : (
            <>
              <p
                className="truncate text-[13px] font-semibold"
                style={{ color: "#f4f5f3" }}
                title={filename}
              >
                {filename}
              </p>
              <p
                className="truncate text-[11px]"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                {mime} · {formatBytes(bytes)}
                {hasMany ? ` · ${index + 1} / ${items.length}` : ""}
              </p>
            </>
          )}
        </div>
        {canRename &&
          (editing ? (
            <>
              <LightboxIconButton label="Speichern" onClick={() => void commitRename()}>
                {renameBusy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
              </LightboxIconButton>
              <LightboxIconButton label="Abbrechen" onClick={() => setEditing(false)}>
                <X className="size-4" />
              </LightboxIconButton>
            </>
          ) : (
            <LightboxIconButton
              label="Umbenennen"
              onClick={() => {
                setDraftName(filename);
                setEditing(true);
              }}
            >
              <Pencil className="size-4" />
            </LightboxIconButton>
          ))}
        <LightboxIconButton
          label={isFs ? "Vollbild verlassen" : "Vollbild"}
          onClick={toggleFullscreen}
        >
          {isFs ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </LightboxIconButton>
        {onMove && canMove?.(item) && (
          <LightboxIconButton
            label={item.source === "local" ? "In Cloud verschieben" : "Lokal speichern"}
            onClick={() => void onMove(item)}
          >
            {moveBusy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : item.source === "local" ? (
              <CloudUpload className="size-4" />
            ) : (
              <HardDriveDownload className="size-4" />
            )}
          </LightboxIconButton>
        )}
        <ExportMenu
          target={exportTargetFor(item)}
          onError={(msg) => setExportError(msg)}
          trigger={({ onClick, ...aria }) => (
            <button
              type="button"
              onClick={onClick}
              aria-label="Exportieren"
              title="Exportieren"
              {...aria}
              className="grid size-9 place-items-center rounded-xl transition-colors"
              style={{
                background: "rgba(255,255,255,0.06)",
                color: "#f4f5f3",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <Download className="size-4" />
            </button>
          )}
        />
        {printRequest && onRequestPrint && (
          <LightboxIconButton label="Drucken" onClick={() => onRequestPrint(printRequest)}>
            <Printer className="size-4" />
          </LightboxIconButton>
        )}
        {onRequestPoster && (mediaKind(mime) === "image" || mime === "application/pdf") && (
          <LightboxIconButton
            label="Als Poster drucken"
            onClick={() =>
              onRequestPoster(
                item,
                printRequest?.job.kind === "pdf" ? printRequest.job.currentPage : 1,
              )
            }
          >
            <Grid2x2 className="size-4" />
          </LightboxIconButton>
        )}
        <LightboxIconButton label="Löschen" tone="danger" onClick={() => void onDelete(item)}>
          <Trash2 className="size-4" />
        </LightboxIconButton>
        <LightboxIconButton label="Schließen" onClick={onClose}>
          <X className="size-4" />
        </LightboxIconButton>
      </header>

      {exportError && (
        <div
          role="status"
          className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-2xl px-4 py-2 text-[12px] font-semibold shadow-lg"
          style={{
            background: "rgba(255,107,107,0.18)",
            color: "#ffb4b4",
            border: "1px solid rgba(255,107,107,0.35)",
            backdropFilter: "blur(8px)",
          }}
        >
          {exportError}
        </div>
      )}

      {/* stage */}
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-3 sm:px-6"
        onClick={(e) => e.stopPropagation()}
      >
        {hasMany && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="Vorheriges Medium"
            className="absolute left-3 top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-full transition-colors hover:bg-white/10"
            style={{
              background: "rgba(10,12,8,0.65)",
              color: "#f4f5f3",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <ChevronLeft className="size-6" />
          </button>
        )}

        <div className="flex h-full w-full items-center justify-center">
          <LightboxStage key={item.id} item={item} onPrintReady={setPrintRequest} />
        </div>

        {hasMany && (
          <button
            type="button"
            onClick={goNext}
            aria-label="Nächstes Medium"
            className="absolute right-3 top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-full transition-colors hover:bg-white/10"
            style={{
              background: "rgba(10,12,8,0.65)",
              color: "#f4f5f3",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <ChevronRight className="size-6" />
          </button>
        )}
      </div>
    </div>
  );
}

function LightboxIconButton({
  label,
  children,
  onClick,
  tone = "neutral",
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  tone?: "neutral" | "danger";
}) {
  const danger = tone === "danger";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid size-9 place-items-center rounded-xl transition-colors"
      style={{
        background: danger ? "rgba(255,107,107,0.14)" : "rgba(255,255,255,0.06)",
        color: danger ? "#ff8a8a" : "#f4f5f3",
        border: `1px solid ${danger ? "rgba(255,107,107,0.30)" : "rgba(255,255,255,0.10)"}`,
      }}
    >
      {children}
    </button>
  );
}

/** Resolves a usable src for an item and renders the right viewer. */
function LightboxStage({
  item,
  onPrintReady,
}: {
  item: UnifiedItem;
  onPrintReady?: (req: PrintRequest | null) => void;
}) {
  if (item.source === "cloud") {
    return <CloudStage item={item.data} onPrintReady={onPrintReady} />;
  }
  return <LocalStage item={item.data} onPrintReady={onPrintReady} />;
}

function CloudStage({
  item,
  onPrintReady,
}: {
  item: MediaItem;
  onPrintReady?: (req: PrintRequest | null) => void;
}) {
  const kind = mediaKind(item.mime);
  const view = useQuery({
    queryKey: ["media", "view", item.id],
    queryFn: () => apiFetch<ViewUrlResponse>(`/api/v1/media/${item.id}/view-url`),
    // Images/videos can render straight from the public CDN URL; PDFs and
    // anything else need a short-lived signed URL because the iframe sends
    // no auth headers.
    enabled: kind !== "image" && kind !== "video",
  });
  const [useSigned, setUseSigned] = useState(false);

  const src = useSigned || (kind !== "image" && kind !== "video") ? view.data?.url : item.publicUrl;

  return (
    <StageContent
      kind={kind}
      mime={item.mime}
      src={src ?? null}
      filename={item.filename}
      loading={view.isLoading && kind === "other"}
      onMediaError={() => !useSigned && setUseSigned(true)}
      onPrintReady={onPrintReady}
    />
  );
}

function LocalStage({
  item,
  onPrintReady,
}: {
  item: LocalMediaItem;
  onPrintReady?: (req: PrintRequest | null) => void;
}) {
  const kind = mediaKind(item.mime);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    void localMediaToBlobUrl(item)
      .then((u) => {
        if (!active) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [item.id]);

  if (failed) {
    return (
      <p className="text-[12px]" style={{ color: "var(--color-error, #ff6b6b)" }}>
        Datei konnte nicht gelesen werden.
      </p>
    );
  }

  return (
    <StageContent
      kind={kind}
      mime={item.mime}
      src={url}
      filename={item.filename}
      loading={!url}
      onPrintReady={onPrintReady}
    />
  );
}

function StageContent({
  kind,
  mime,
  src,
  filename,
  loading,
  onMediaError,
  onPrintReady,
}: {
  kind: "image" | "video" | "other";
  mime: string;
  src: string | null;
  filename: string;
  loading?: boolean;
  onMediaError?: () => void;
  /**
   * Reports a print-ready payload (image src, or PDF src + current
   * page) up to the Lightbox header so a single "Drucken" button can
   * sit in the toolbar regardless of media type. `null` means the
   * current stage isn't printable (video / unknown / not loaded yet).
   */
  onPrintReady?: (req: PrintRequest | null) => void;
}) {
  // Image stages become printable as soon as `src` resolves. For PDFs
  // we clear until the PdfViewer's `onInfoChange` fires with the live
  // page count. Videos / unknown → cleared. This effect is the SOLE
  // owner of `printRequest` for non-PDF media; the parent must not
  // pre-clear on item change (that would race this mount effect).
  useEffect(() => {
    if (!onPrintReady) return;
    if (kind === "image" && src) {
      onPrintReady({ title: filename, job: { kind: "image", src } });
    } else {
      // image without src yet, video, pdf (waiting for onInfoChange), other
      onPrintReady(null);
    }
  }, [kind, mime, src, filename, onPrintReady]);
  if (loading) {
    return <Loader2 className="size-8 animate-spin text-white/80" />;
  }
  if (!src) {
    return (
      <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
        Vorschau nicht verfügbar.
      </p>
    );
  }
  if (kind === "image") {
    return (
      <div className="relative flex h-full w-full items-center justify-center">
        <img
          src={src}
          alt={filename}
          className="max-h-full max-w-full object-contain"
          style={{ maxHeight: "calc(100vh - 7rem)" }}
          onError={onMediaError}
        />
      </div>
    );
  }
  if (kind === "video") {
    return (
      <video
        controls
        src={src}
        className="max-h-full max-w-full object-contain"
        style={{ maxHeight: "calc(100vh - 7rem)" }}
        onError={onMediaError}
      />
    );
  }
  if (mime === "application/pdf") {
    return (
      <PdfViewer
        src={src}
        filename={filename}
        hidePrintButton
        onInfoChange={
          onPrintReady
            ? ({ currentPage, totalPages }) =>
                onPrintReady({
                  title: filename,
                  job: {
                    kind: "pdf",
                    src,
                    currentPage,
                    totalPages,
                  },
                })
            : undefined
        }
      />
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold"
      style={{
        background: "var(--color-pitch-lime)",
        color: "#1d3700",
      }}
    >
      <Download className="size-4" /> Datei öffnen
    </a>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

/**
 * Compact storage gauge for the toolbar. Shows `used / limit` and a thin
 * progress bar. Switches to a warning/danger tint at 90% / 100% so the
 * user notices before they start an upload that will fail.
 */
function StorageMeter({ storage }: { storage: StorageResponse | null }) {
  if (!storage || storage.limit <= 0) return null;
  const pct = Math.min(100, (storage.used / storage.limit) * 100);
  const full = pct >= 100;
  const warn = pct >= 90 && !full;
  const color = full ? "#ff6b6b" : warn ? "#ffb84d" : "var(--color-pitch-lime)";
  const pctColor = full ? "#ff8a8a" : warn ? "#ffb84d" : "#f4f5f3";

  // Donut geometry — keep `r` small enough that stroke + ring fit in a
  // 28×28 box. Circumference = 2πr ≈ 75.4 for r=12.
  const r = 12;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);

  return (
    <div
      className="flex items-center gap-2 rounded-xl px-2.5 py-1.5"
      style={{
        background: "rgba(10,12,8,0.45)",
        border: `1px solid ${
          full
            ? "rgba(255,107,107,0.35)"
            : warn
              ? "rgba(255,184,77,0.30)"
              : "rgba(255,255,255,0.08)"
        }`,
      }}
      title={`Cloud-Speicher: ${formatBytes(storage.used)} von ${formatBytes(storage.limit)} belegt (${pct.toFixed(0)} %)`}
    >
      <Cloud className="size-6 shrink-0" style={{ color: "var(--color-on-surface-variant)" }} />
      <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true" className="shrink-0">
        <circle cx="14" cy="14" r={r} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="3" />
        <circle
          cx="14"
          cy="14"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 14 14)"
          style={{ transition: "stroke-dashoffset 300ms ease" }}
        />
        <text
          x="14"
          y="14.5"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="8"
          fontWeight="700"
          fill={pctColor}
        >
          {Math.round(pct)}
        </text>
      </svg>
      <div className="flex flex-col leading-tight">
        <span className="text-[11px] font-semibold" style={{ color: "#f4f5f3" }}>
          {formatBytes(storage.used)}
        </span>
        <span className="text-[10px]" style={{ color: "var(--color-on-surface-variant)" }}>
          von {formatBytes(storage.limit)}
        </span>
      </div>
    </div>
  );
}

/**
 * Blocking error popup. Used for quota / "file too big" failures where
 * the user has to acknowledge before doing anything else.
 */
function ErrorDialog({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-300 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape" || e.key === "Enter") onClose();
      }}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-md rounded-3xl border border-white/10 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-xl"
            style={{
              background: "rgba(255,107,107,0.14)",
              color: "#ff8a8a",
              border: "1px solid rgba(255,107,107,0.30)",
            }}
          >
            <AlertTriangle className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-bold" style={{ color: "#f4f5f3" }}>
              Upload nicht möglich
            </h3>
            <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              {message}
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            autoFocus
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors"
            style={{
              background: "var(--color-pitch-lime)",
              color: "#1d3700",
            }}
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <section className="glass-floating grid place-items-center rounded-3xl px-6 py-16">
      <div
        className="grid size-14 place-items-center rounded-2xl"
        style={{
          background: "linear-gradient(140deg, rgba(151,252,0,0.25) 0%, rgba(151,252,0,0.05) 100%)",
          color: "var(--color-pitch-lime)",
        }}
      >
        <ImageIcon className="size-7" />
      </div>
      <p
        className="mt-4 max-w-md text-center text-[13px]"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {text}
      </p>
    </section>
  );
}

function mediaKind(mime: string): "image" | "video" | "other" {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "other";
}

/** Strip the trailing `.ext` from a filename for display purposes. */
function stripExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  // Treat a leading dot ("dotfile") as part of the name, not an extension.
  if (dot <= 0) return name;
  // Sanity-check: only strip what looks like a real extension (≤ 5 chars,
  // alphanumeric). "report.final" stays "report.final".
  const ext = name.slice(dot + 1);
  if (!/^[A-Za-z0-9]{1,5}$/.test(ext)) return name;
  return name.slice(0, dot);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}
